/* ============================================
   Artemis Theme — Customizer Canvas Engine
   ============================================
   Per-instance canvas. Each product panel in the
   Apparel Designer gets its own ArtemisCanvas.

   Two modes:
     - 'same'      : one logo, prints on N selected locations
     - 'different' : a distinct logo per location

   Art images source from R2 URLs (set crossOrigin
   'anonymous' so canvas export stays untainted).
   ============================================ */

(function () {
  'use strict';

  var CANVAS_SIZE = 500;

  function ArtemisCanvas(canvasEl, locations, mockups, initialColor, initialView) {
    this.canvas = canvasEl;
    this.ctx = canvasEl.getContext('2d');
    this.dpr = window.devicePixelRatio || 1;
    this.locations = locations || [];
    this.mockups = mockups || {};
    this.activeColor = this._resolveColor(initialColor);
    this.activeView = initialView || this._firstViewForColor(this.activeColor) || 'front';
    this.activeLocationId = null;

    this.garmentImage = null;
    this.dirty = true;
    this.animFrameId = null;

    // Art state
    this.mode = 'same';
    this.sharedLogo = null;      // { img, url, key, enabledLocationIds: [] }
    this.perSideArt = {};        // { [locationId]: { img, url, key } }

    // Example custom-name text drawn into the name placement (preview only —
    // not part of the persisted canvas state).
    this.nameText = '';
    this.nameColor = '#1A1A1A';
    this.namePlaceholder = false;

    this.onStateChange = null;

    this.canvas.width = CANVAS_SIZE * this.dpr;
    this.canvas.height = CANVAS_SIZE * this.dpr;
    this.canvas.style.width = '100%';
    this.canvas.style.height = 'auto';
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    this._loadGarmentImage();
    this._startRenderLoop();
  }

  ArtemisCanvas.prototype._resolveColor = function (color) {
    if (color && this.mockups[color]) return color;
    var keys = Object.keys(this.mockups);
    return keys.length ? keys[0] : '';
  };

  ArtemisCanvas.prototype._firstViewForColor = function (color) {
    var views = this.mockups[color];
    if (!views) return null;
    var keys = Object.keys(views);
    return keys.length ? keys[0] : null;
  };

  ArtemisCanvas.prototype.destroy = function () {
    if (this.animFrameId) cancelAnimationFrame(this.animFrameId);
  };

  /* ---- Garment Image ---- */
  ArtemisCanvas.prototype._loadGarmentImage = function () {
    var self = this;
    var views = this.mockups[this.activeColor] || {};
    var url = views[this.activeView];
    if (!url) {
      this.garmentImage = null;
      this.dirty = true;
      return;
    }
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      self.garmentImage = img;
      self.dirty = true;
    };
    img.src = url;
  };

  /* ---- Color ---- */
  ArtemisCanvas.prototype.setColor = function (colorName) {
    var resolved = this._resolveColor(colorName);
    if (resolved === this.activeColor) return;
    this.activeColor = resolved;
    this.garmentImage = null;
    var views = this.mockups[this.activeColor] || {};
    if (!views[this.activeView]) {
      var firstView = this._firstViewForColor(this.activeColor);
      if (firstView) this.activeView = firstView;
    }
    this._loadGarmentImage();
    this.dirty = true;
    this._notifyStateChange();
  };

  /* ---- Mode ---- */
  ArtemisCanvas.prototype.setMode = function (mode) {
    if (mode !== 'same' && mode !== 'different') return;
    if (this.mode === mode) return;
    this.mode = mode;
    this.dirty = true;
    this._notifyStateChange();
  };

  /* ---- Shared logo (mode = 'same') ---- */
  ArtemisCanvas.prototype.setSharedLogo = function (img, url, key, previewUrl) {
    var existingLocIds = (this.sharedLogo && this.sharedLogo.enabledLocationIds) || [];
    if (!existingLocIds.length && this.activeLocationId) {
      existingLocIds = [this.activeLocationId];
    }
    this.sharedLogo = {
      img: img,
      url: url,
      previewUrl: previewUrl || (img && img.src) || url,
      key: key || '',
      enabledLocationIds: existingLocIds.slice(),
    };
    this.dirty = true;
    this._notifyStateChange();
  };

  ArtemisCanvas.prototype.clearSharedLogo = function () {
    this.sharedLogo = null;
    this.dirty = true;
    this._notifyStateChange();
  };

  ArtemisCanvas.prototype._viewFor = function (locationId) {
    for (var i = 0; i < this.locations.length; i++) {
      if (this.locations[i].id === locationId) return this.locations[i].view;
    }
    return null;
  };

  ArtemisCanvas.prototype.toggleLocationEnabled = function (locationId) {
    if (!this.sharedLogo) return;
    var list = this.sharedLogo.enabledLocationIds;
    var idx = list.indexOf(locationId);
    if (idx >= 0) {
      list.splice(idx, 1);
    } else {
      var view = this._viewFor(locationId);
      if (view) {
        for (var i = list.length - 1; i >= 0; i--) {
          if (this._viewFor(list[i]) === view) list.splice(i, 1);
        }
      }
      list.push(locationId);
    }
    this.dirty = true;
    this._notifyStateChange();
  };

  ArtemisCanvas.prototype.isLocationEnabled = function (locationId) {
    return !!(this.sharedLogo && this.sharedLogo.enabledLocationIds.indexOf(locationId) >= 0);
  };

  /* ---- Per-side art (mode = 'different') ---- */
  ArtemisCanvas.prototype.setPerSideArt = function (locationId, img, url, key, previewUrl) {
    if (!locationId) return;
    var view = this._viewFor(locationId);
    if (view) {
      var self = this;
      Object.keys(this.perSideArt).forEach(function (id) {
        if (id !== locationId && self._viewFor(id) === view) delete self.perSideArt[id];
      });
    }
    this.perSideArt[locationId] = {
      img: img,
      url: url,
      previewUrl: previewUrl || (img && img.src) || url,
      key: key || '',
    };
    this.dirty = true;
    this._notifyStateChange();
  };

  ArtemisCanvas.prototype.clearPerSideArt = function (locationId) {
    if (!locationId) return;
    delete this.perSideArt[locationId];
    this.dirty = true;
    this._notifyStateChange();
  };

  /* ---- Active location (UI highlight) ---- */
  ArtemisCanvas.prototype.setLocation = function (locationId) {
    this.activeLocationId = locationId;
    this.dirty = true;
    this._notifyStateChange();
  };

  ArtemisCanvas.prototype.getActiveLocation = function () {
    if (!this.activeLocationId) return null;
    for (var i = 0; i < this.locations.length; i++) {
      if (this.locations[i].id === this.activeLocationId) return this.locations[i];
    }
    return null;
  };

  ArtemisCanvas.prototype.getLocationsForView = function (view) {
    view = view || this.activeView;
    return this.locations.filter(function (loc) { return loc.view === view; });
  };

  /* ---- View ---- */
  ArtemisCanvas.prototype.setView = function (viewName) {
    if (this.activeView === viewName) return;
    this.activeView = viewName;
    this.garmentImage = null;
    this._loadGarmentImage();
    var loc = this.getActiveLocation();
    if (loc && loc.view !== viewName) this.activeLocationId = null;
    this.dirty = true;
    this._notifyStateChange();
  };

  ArtemisCanvas.prototype.getAvailableViews = function () {
    var views = {};
    for (var color in this.mockups) {
      if (!this.mockups.hasOwnProperty(color)) continue;
      for (var v in this.mockups[color]) {
        if (this.mockups[color].hasOwnProperty(v)) views[v] = true;
      }
    }
    return Object.keys(views);
  };

  /* ---- Render loop ---- */
  ArtemisCanvas.prototype._startRenderLoop = function () {
    var self = this;
    function loop() {
      if (self.dirty) {
        self._render();
        self.dirty = false;
      }
      self.animFrameId = requestAnimationFrame(loop);
    }
    loop();
  };

  ArtemisCanvas.prototype._render = function () {
    var ctx = this.ctx;
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    this._renderGarment(ctx);
    this._renderLocationZones(ctx);
    this._renderArtForCurrentView(ctx);
    this._renderNameText(ctx);
  };

  /* ---- Custom name preview ---- */
  ArtemisCanvas.prototype.setNameText = function (text, color, isPlaceholder) {
    this.nameText = text || '';
    this.nameColor = color || '#1A1A1A';
    this.namePlaceholder = !!isPlaceholder;
    this.dirty = true;
  };

  ArtemisCanvas.prototype.clearNameText = function () {
    if (!this.nameText && !this.namePlaceholder) return;
    this.nameText = '';
    this.namePlaceholder = false;
    this.dirty = true;
  };

  ArtemisCanvas.prototype._renderNameText = function (ctx) {
    if (!this.nameText) return;
    var loc = null;
    for (var i = 0; i < this.locations.length; i++) {
      if (this.locations[i].id === 'name-back' && this.locations[i].view === this.activeView) {
        loc = this.locations[i];
        break;
      }
    }
    if (!loc) return;

    var text = String(this.nameText).toUpperCase();
    var maxW = loc.w * 0.92;
    var size = Math.min(loc.h * 0.85, 44);
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Shrink the font until the name fits the box width.
    while (size > 6) {
      ctx.font = '700 ' + size + 'px Arial, sans-serif';
      if (ctx.measureText(text).width <= maxW) break;
      size -= 1;
    }
    var cx = loc.x + loc.w / 2;
    var cy = loc.y + loc.h / 2;
    if (this.namePlaceholder) ctx.globalAlpha = 0.4;
    // A soft dark outline keeps light name colors legible on light garments.
    ctx.lineWidth = Math.max(1, size / 16);
    ctx.strokeStyle = 'rgba(0,0,0,0.35)';
    ctx.strokeText(text, cx, cy);
    ctx.fillStyle = this.nameColor || '#1A1A1A';
    ctx.fillText(text, cx, cy);
    ctx.restore();
  };

  ArtemisCanvas.prototype._renderGarment = function (ctx) {
    if (!this.garmentImage) return;
    var img = this.garmentImage;
    // Garment flats are SanMar's product photos normalized upstream: cropped to
    // content and centered on a 500×500 frame. The print_locations boxes are
    // authored by the vision model against that SAME 500×500 frame, so a square
    // flat contain-fit here is an identity transform — the garment lands pixel
    // for pixel where the boxes expect it, and the overlay registers exactly.
    // Contain-fit (not stretch) keeps any non-square fallback undistorted.
    var scale = Math.min(CANVAS_SIZE / img.width, CANVAS_SIZE / img.height);
    var w = img.width * scale;
    var h = img.height * scale;
    var x = (CANVAS_SIZE - w) / 2;
    var y = (CANVAS_SIZE - h) / 2;
    ctx.drawImage(img, x, y, w, h);
  };

  ArtemisCanvas.prototype._renderLocationZones = function (ctx) {
    var viewLocations = this.getLocationsForView();
    for (var i = 0; i < viewLocations.length; i++) {
      var loc = viewLocations[i];
      var isActive = loc.id === this.activeLocationId;
      ctx.save();
      if (isActive) {
        ctx.strokeStyle = 'rgba(212, 168, 83, 0.8)';
        ctx.lineWidth = 2;
        ctx.setLineDash([]);
        ctx.fillStyle = 'rgba(212, 168, 83, 0.08)';
        ctx.fillRect(loc.x, loc.y, loc.w, loc.h);
      } else {
        ctx.strokeStyle = 'rgba(212, 168, 83, 0.3)';
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 3]);
      }
      ctx.strokeRect(loc.x, loc.y, loc.w, loc.h);
      ctx.setLineDash([]);
      ctx.font = '10px Arial';
      ctx.fillStyle = isActive ? 'rgba(212, 168, 83, 0.9)' : 'rgba(212, 168, 83, 0.5)';
      ctx.fillText(loc.label, loc.x + 3, loc.y - 3);
      ctx.restore();
    }
  };

  ArtemisCanvas.prototype._renderArtAt = function (ctx, loc, img) {
    if (!img || !loc) return;
    var scale = Math.min(loc.w / img.width, loc.h / img.height);
    var w = img.width * scale;
    var h = img.height * scale;
    // Center the art horizontally, but anchor it to the TOP of the box (not the
    // vertical center) so a logo sits high in the placement zone rather than
    // floating in the middle of a tall full-front/full-back area.
    var x = loc.x + (loc.w - w) / 2;
    var y = loc.y;
    ctx.drawImage(img, x, y, w, h);
  };

  ArtemisCanvas.prototype._renderArtForCurrentView = function (ctx) {
    var viewLocations = this.getLocationsForView();
    for (var i = 0; i < viewLocations.length; i++) {
      var loc = viewLocations[i];
      var img = this._imageForLocation(loc.id);
      if (img) this._renderArtAt(ctx, loc, img);
    }
  };

  ArtemisCanvas.prototype._imageForLocation = function (locationId) {
    if (this.mode === 'same') {
      if (!this.sharedLogo || !this.sharedLogo.img) return null;
      if (this.sharedLogo.enabledLocationIds.indexOf(locationId) < 0) return null;
      return this.sharedLogo.img;
    }
    var art = this.perSideArt[locationId];
    return art ? art.img : null;
  };

  /* ---- Export ---- */
  ArtemisCanvas.prototype.toPreviewDataURL = function () {
    var ctx = this.ctx;
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    this._renderGarment(ctx);
    this._renderArtForCurrentView(ctx);
    this._renderNameText(ctx);
    var dataUrl = this.canvas.toDataURL('image/jpeg', 0.4);
    this.dirty = true; // trigger re-render with zones on next frame
    return dataUrl;
  };

  ArtemisCanvas.prototype.getState = function () {
    var loc = this.getActiveLocation();
    var state = {
      view: this.activeView,
      color: this.activeColor,
      locationId: this.activeLocationId,
      locationLabel: loc ? loc.label : '',
      mode: this.mode,
    };
    if (this.mode === 'same') {
      state.sharedLogo = this.sharedLogo
        ? {
            url: this.sharedLogo.url,
            previewUrl: this.sharedLogo.previewUrl || this.sharedLogo.url,
            key: this.sharedLogo.key,
            enabledLocationIds: this.sharedLogo.enabledLocationIds.slice(),
          }
        : null;
    } else {
      var out = {};
      for (var id in this.perSideArt) {
        if (this.perSideArt.hasOwnProperty(id)) {
          out[id] = {
            url: this.perSideArt[id].url,
            previewUrl: this.perSideArt[id].previewUrl || this.perSideArt[id].url,
            key: this.perSideArt[id].key,
          };
          // Embroidery digitize result (TrueView preview, stitch file URLs,
          // warnings) — set by the designer orchestrator, persisted so it
          // rehydrates without a fresh digitize call.
          if (this.perSideArt[id].digitized) out[id].digitized = this.perSideArt[id].digitized;
        }
      }
      state.perSideArt = out;
    }
    return state;
  };

  ArtemisCanvas.prototype.getSummary = function () {
    var self = this;
    function labelFor(id) {
      for (var i = 0; i < self.locations.length; i++) {
        if (self.locations[i].id === id) return self.locations[i].label;
      }
      return id;
    }
    if (this.mode === 'same') {
      if (!this.sharedLogo) return 'No customization';
      var names = this.sharedLogo.enabledLocationIds.map(labelFor);
      return names.length ? names.join(' + ') + ': shared logo' : 'Shared logo (no location selected)';
    }
    var parts = [];
    for (var id in this.perSideArt) {
      if (this.perSideArt.hasOwnProperty(id)) parts.push(labelFor(id) + ': custom');
    }
    return parts.length ? parts.join(', ') : 'No customization';
  };

  ArtemisCanvas.prototype.hasAnyArt = function () {
    if (this.mode === 'same') return !!(this.sharedLogo && this.sharedLogo.enabledLocationIds.length);
    for (var id in this.perSideArt) {
      if (this.perSideArt.hasOwnProperty(id)) return true;
    }
    return false;
  };

  ArtemisCanvas.prototype._notifyStateChange = function () {
    if (this.onStateChange) this.onStateChange(this.getState());
  };

  window.ArtemisCanvas = ArtemisCanvas;
})();
